import type { TuplRouteStop } from './useTuplApi'

export type GeoPoint = { lat?: number | null; lon?: number | null }

/** City-wide live view is ~zoom 12; a 500 m pan is invisible without tightening. */
export const BUS_FOCUS_MIN_ZOOM = 15

function pair(p?: GeoPoint | null): [number, number] | null {
  if (p == null) return null
  const { lat, lon } = p
  if (lat == null || lon == null) return null
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  return [lon, lat]
}

/** Live GPS first; alert/breakdown pin if the bus has no point yet. */
export function resolveBusFocus(
  vehicle?: GeoPoint | null,
  fallback?: GeoPoint | null,
): [number, number] | null {
  return pair(vehicle) ?? pair(fallback)
}

/** A stable identity for a replay's own trail, used by TuplLiveMap's fit-bounds-to-trip
 *  effect to fit the camera once per distinct trail rather than once per render.
 *
 *  Returns null for anything that should never trigger a fit: a replay with no `alerts`
 *  field at all, or a trail too short to bound. For everything else (a static trip-detail
 *  replay, a breakdown-evidence replay, or a duty
 *  replay ticking a `playhead` every 200ms) it returns a key built from the trail's own
 *  endpoints, which is IDENTICAL across ticks of the same duty session (the underlying
 *  `duty.points` don't change, only the playhead does) and DIFFERENT whenever a genuinely
 *  new trail arrives (a new clip, or a remounted duty modal for a different vehicle). The
 *  caller fits exactly when this key changes from the previous one it saw. */
export function replayFitKey(
  replay: { alerts?: unknown[] | null; line: [number, number][] } | null | undefined,
): string | null {
  if (!replay?.alerts || replay.line.length < 2) return null
  const first = replay.line[0]
  const last = replay.line[replay.line.length - 1]
  return `${replay.line.length}:${first?.join(',')}:${last?.join(',')}`
}

/** Which position TuplLiveMap's next-stop highlight is computed from (Command Centre
 *  rework Phase 4) -- extracted as its own pure function so the precedence rule is directly
 *  testable without mounting the WebGL map. SPEC.md: "`replay.playhead` wins when both
 *  could apply" -- the duty and trip-detail modals mount this map with `selected={null}
 *  vehicles={[]}`, so a replay's own scrub position is the more specific signal whenever
 *  one exists; the selected live vehicle's GPS fix is the fallback for the plain live wall,
 *  which never has a `replay` at all. */
export function nextStopPosition(
  selectedVehicle: GeoPoint | null | undefined,
  playhead: [number, number] | null | undefined,
): { lat: number; lon: number } {
  if (playhead) return { lat: playhead[1], lon: playhead[0] }
  return { lat: selectedVehicle?.lat ?? Number.NaN, lon: selectedVehicle?.lon ?? Number.NaN }
}

export interface StopBeadFeature {
  type: 'Feature'
  properties: { name: string; seq: number; direction: string; next: boolean }
  geometry: { type: 'Point'; coordinates: [number, number] }
}

/** The GeoJSON features TuplLiveMap pushes to its `tupl-route-stops` source, with the
 *  `next` flag the `tupl-route-stop-next` layer filters on -- extracted as its own pure
 *  function (Command Centre rework Phase 4) so the highlight-flag wiring is directly
 *  testable without mounting the WebGL map. A stop with non-finite coordinates is dropped
 *  entirely, same as every other overlay source in this codebase.
 *
 *  Flagged by `${direction}:${seq}` (the same identity key `stopArrivalTimes` already
 *  uses), not by reference (`s === nextStop`) -- both reads happen to come from the same
 *  `routeStops` state today, but a reference comparison would silently lose the highlight
 *  the moment either side is ever cloned or re-mapped between the two call sites, with no
 *  error to catch it. */
export function stopBeadFeatures(
  stops: TuplRouteStop[], nextStop: TuplRouteStop | null,
): StopBeadFeature[] {
  const nextKey = nextStop ? `${nextStop.direction}:${nextStop.seq}` : null
  return stops
    .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon))
    .map((s) => ({
      type: 'Feature',
      properties: {
        name: s.name, seq: s.seq, direction: s.direction,
        next: nextKey !== null && `${s.direction}:${s.seq}` === nextKey,
      },
      geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
    }))
}
